import React, { useState, useRef, useEffect, useImperativeHandle } from 'react';
import { useParams, Link } from 'react-router-dom';
import destinationsData from '../data/destinationsData';
import { venuesData } from '../data/venuesData';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/en';
import { translations as esTranslations } from '../translations/es';
import Footer from '../components/Footer';
import AssemblageInfo from '../components/AssemblageInfo';
import FullBleedImage from '../components/FullBleedImage';
import { trackConsultationClick } from '../utils/analytics';

// Why Destination Section Component
const WhyDestinationSection = ({ destinationData, language }) => (
  <section id="why-destination" className="max-w-6xl mx-auto px-4 sm:px-6 py-2 md:py-12 scroll-mt-[9.5rem]">
    <h2 className="text-3xl md:text-5xl font-playfair mb-6 md:mb-12 text-center text-[#181c24] tracking-tight">
      {language === 'en' ? `Why Choose ${destinationData.name}?` : `¿Por qué elegir ${destinationData.name}?`}
    </h2>
    {/* Experience the Magic - full width */}
    <div className="mb-2 md:mb-10">
      <h3 className="text-2xl md:text-3xl font-playfair mb-1 md:mb-3 text-[#181c24]">{language === 'en' ? `Experience the Magic of ${destinationData.name}` : `Vive la Magia de ${destinationData.name}`}</h3>
      <p className="text-xs md:text-sm text-[#181c24]/90 font-light leading-relaxed">
        {destinationData.magicExperience}
      </p>
    </div>
    {/* Image full width */}
    <div className="flex justify-center mb-2 md:mb-10">
      <img
        src={destinationData.magicImage || destinationData.image}
        alt={`Climate and scenery in ${destinationData.name}`}
        className="w-full max-w-4xl rounded-lg shadow-lg object-cover object-center border border-[#e5d9c3] h-56 md:h-96"
        loading="lazy"
        onError={e => { e.target.onerror = null; e.target.src = destinationData.heroImage; }}
      />
    </div>
    {/* Two-column grid for blurbs */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-12">
      {/* Climate & Seasons */}
      <div>
        <h3 className="text-xl md:text-2xl font-playfair mb-1 md:mb-3 text-[#181c24]">{language === 'en' ? 'Climate & Seasons' : 'Clima y Estaciones'}</h3>
        <p className="text-xs md:text-sm text-[#181c24]/90 font-light leading-relaxed">
          {destinationData.climateSeasons}
        </p>
        {destinationData.favoriteWeddingSeason && (
          <div className="bg-[#f5f0e6] p-2 md:p-6 rounded-lg mt-2 md:mt-4 flex items-center justify-start">
            <span className="inline-block w-2 h-8 bg-[#bfa76a] rounded-full mr-4" />
            {(() => {
              const season = destinationData.favoriteWeddingSeason;
              let match = season.match(/^(.*\b(is|es)\b)(.*)$/i);
              if (match) {
                return (
                  <span className="text-base md:text-lg font-playfair text-[#181c24] font-bold">
                    {match[1].trim()}<span className="text-[#bfa76a] font-bold">{match[3]}</span>
                  </span>
                );
              }
              return <span className="text-base md:text-lg font-playfair text-[#181c24] font-bold">{season}</span>;
            })()}
          </div>
        )}
      </div>
      {/* Seamless Accessibility */}
      <div>
        <h3 className="text-xl md:text-2xl font-playfair mb-1 md:mb-3 text-[#181c24]">{language === 'en' ? 'Seamless Accessibility' : 'Accesibilidad sin Esfuerzo'}</h3>
        <p className="text-xs md:text-sm text-[#181c24]/90 font-light leading-relaxed">
          {destinationData.accessibilityDetails}
        </p>
      </div>
    </div>
  </section>
);

// Gold Arrow component for carousels
const Arrow = ({ direction, onClick }) => (
    <button
        onClick={onClick}
        className={`absolute top-1/2 -translate-y-1/2 z-20 bg-white/80 backdrop-blur-sm rounded-full p-1 shadow-md hover:bg-white transition-all duration-300 ${direction === 'left' ? '-left-4' : '-right-4'}`}
    >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#bfa76a]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            {direction === 'left' ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            )}
        </svg>
    </button>
);

// Wrapper for carousels to add arrows
const CarouselWithArrows = React.forwardRef(({ children, showArrows, items, renderThumbnail }, ref) => {
    const carouselRef = useRef(null);
    const [activeIndex, setActiveIndex] = useState(0);

    const scrollToIndex = (index) => {
        if (!carouselRef.current || !items || items.length === 0) return;
        const newIndex = (index + items.length) % items.length;
        carouselRef.current.scrollToIndex(newIndex);
        setActiveIndex(newIndex);
    };
    
    const scroll = (direction) => {
        scrollToIndex(activeIndex + direction);
    };

    const childWithRef = React.cloneElement(children, {
        ...children.props,
        ref: carouselRef,
        onActiveIndexChange: setActiveIndex,
    });

    return (
        <div className="relative" ref={ref}>
            {childWithRef}
            <div className={`transition-opacity duration-300 ${showArrows ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                <Arrow direction="left" onClick={() => scroll(-1)} />
                <Arrow direction="right" onClick={() => scroll(1)} />
            </div>
            {items && renderThumbnail && (
                <div className="md:hidden">
                    <ThumbnailStrip
                        items={items}
                        renderItem={renderThumbnail}
                        activeIndex={activeIndex}
                        onThumbnailClick={scrollToIndex}
                    />
                </div>
            )}
        </div>
    );
});

// Simple horizontal scroll carousel for mobile
const SimpleCarousel = React.forwardRef(({ items, renderItem, itemWidth, isInfinite, onActiveIndexChange }, ref) => {
  const canBeInfinite = isInfinite && items && items.length > 2;
  const displayItems = canBeInfinite ? [...items, ...items, ...items] : items;
  const scrollContainerRef = useRef(null); // Internal ref for the DOM element

  // Expose a `scrollToIndex` method to the parent component
  useImperativeHandle(ref, () => ({
    scrollToIndex: (index) => {
      const scrollContainer = scrollContainerRef.current;
      if (!scrollContainer || !items || items.length === 0) return;

      let targetIndex = index;
      if (canBeInfinite) {
        targetIndex = items.length + index;
      }

      const targetElement = scrollContainer.children[targetIndex];
      if (targetElement) {
        const scrollLeft = targetElement.offsetLeft - (scrollContainer.offsetWidth - targetElement.offsetWidth) / 2;
        scrollContainer.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      }
    }
  }));

  // This effect handles the infinite scroll logic and active index detection
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    // --- Observer to detect which item is currently centered ---
    let lastActiveIndex = -1;
    const observer = new IntersectionObserver(
        (entries) => {
            const intersectingEntries = entries.filter(e => e.isIntersecting);
            if (intersectingEntries.length === 0) return;

            const mostVisible = intersectingEntries.reduce((a, b) =>
                a.intersectionRatio > b.intersectionRatio ? a : b
            );

            if (onActiveIndexChange && mostVisible.target.dataset.index) {
                const newIndex = parseInt(mostVisible.target.dataset.index, 10) % items.length;
                if (newIndex !== lastActiveIndex) {
                    onActiveIndexChange(newIndex);
                    lastActiveIndex = newIndex;
                }
            }
        },
        { root: scrollContainer, threshold: 0.75 }
    );
    
    Array.from(scrollContainer.children).forEach(child => observer.observe(child));

    // --- Scroll handler for seamless infinite looping ---
    let ticking = false;
    const handleScroll = () => {
        if (!canBeInfinite || ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainer;
            const setWidth = scrollWidth / 3;
            const buffer = 5;

            if (scrollLeft >= scrollWidth - clientWidth - buffer) {
                scrollContainer.style.scrollSnapType = 'none';
                scrollContainer.scrollLeft = scrollLeft - setWidth;
                setTimeout(() => { scrollContainer.style.scrollSnapType = 'x mandatory'; }, 20);
            } else if (scrollLeft <= buffer) {
                scrollContainer.style.scrollSnapType = 'none';
                scrollContainer.scrollLeft = scrollLeft + setWidth;
                setTimeout(() => { scrollContainer.style.scrollSnapType = 'x mandatory'; }, 20);
            }
            ticking = false;
        });
    };

    // Set the initial scroll position to the middle set of items for infinite scroll
    if (canBeInfinite) {
        const setWidth = scrollContainer.scrollWidth / 3;
        if (setWidth > 0 && scrollContainer.scrollLeft === 0) {
            scrollContainer.scrollLeft = setWidth;
        }
        scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    }

    return () => {
        observer.disconnect();
        if (canBeInfinite) {
            scrollContainer.removeEventListener('scroll', handleScroll);
        }
    };
  }, [canBeInfinite, items, onActiveIndexChange]);

  return (
    <div
      ref={scrollContainerRef}
      className="flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-thin scrollbar-thumb-[#bfa76a]/70 scrollbar-track-[#f5f0e6] px-4"
      style={{ scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' }}
    >
      {displayItems.map((item, i) => (
        <div
          key={`${item.id || item.name || ''}-${i}`}
          data-index={i}
          className="snap-center flex-shrink-0"
          style={itemWidth ? { width: itemWidth } : {}}
        >
          {renderItem(item, i)}
        </div>
      ))}
    </div>
  );
});

// Dream Venues Section Component
const DreamVenuesSection = ({ destinationData, language, t, showArrows }) => {
  const venueSlugs = destinationData.weddingVenues || [];
  const venues = venueSlugs.map(slug => venuesData[slug]).filter(Boolean);

  const [itemWidth, setItemWidth] = useState('calc(100vw - 32px)');
  const containerRef = useRef(null);
  const [desktopItemWidth, setDesktopItemWidth] = useState('290px');
  const desktopContainerRef = useRef(null);

  useEffect(() => {
    const calculateWidth = () => {
      if (!containerRef.current) return;
      // Always show 1 card on mobile/tablet views for simplicity and focus
      setItemWidth('calc(100vw - 82px)');
    };

    const timeoutId = setTimeout(calculateWidth, 50);
    window.addEventListener('resize', calculateWidth);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', calculateWidth);
    };
  }, [venues]);

  useEffect(() => {
    const calculateDesktopWidth = () => {
      if (!desktopContainerRef.current) return;
      const containerWidth = desktopContainerRef.current.offsetWidth;
      const gap = 24; // Corresponds to gap-6
      const horizontalPadding = 32; // From CarouselWithArrows's px-4
      const numCards = 3;
      const totalGap = gap * (numCards - 1);
      const cardWidth = (containerWidth - horizontalPadding - totalGap) / numCards;
      if (cardWidth > 0) {
        setDesktopItemWidth(`${cardWidth}px`);
      }
    };
    const timeoutId = setTimeout(calculateDesktopWidth, 50);
    window.addEventListener('resize', calculateDesktopWidth);
    return () => {
        clearTimeout(timeoutId);
        window.removeEventListener('resize', calculateDesktopWidth);
    };
  }, []);

  return (
    <section id="venues" className="max-w-6xl mx-auto px-4 sm:px-6 py-2 md:py-12 scroll-mt-[9.5rem]">
      <h2 className="text-2xl md:text-5xl font-playfair mb-6 md:mb-12 text-center text-[#181c24] tracking-tight">
        {language === 'en' ? `Dream Venues in ${destinationData.name}` : `Lugares de Ensueño en ${destinationData.name}`}
      </h2>
      {/* Mobile & tablet carousel (<1024px) */}
      <div className="lg:hidden">
        <CarouselWithArrows 
            showArrows={showArrows} 
            ref={containerRef}
            items={venues}
            renderThumbnail={(venue, isActive) => (
                <img
                    src={venue.imageUrl}
                    alt={venue.name}
                    className={`w-16 h-16 object-cover rounded-md border-2 ${isActive ? 'border-[#bfa76a]' : 'border-transparent'} transition-all`}
                />
            )}
        >
          <SimpleCarousel
            items={venues}
            isInfinite
            itemWidth={itemWidth}
            renderItem={(venue, i) => (
              <div className="bg-white rounded-none shadow-md h-full flex flex-col w-full">
                <div className="w-full h-72">
                  <img
                    src={venue.imageUrl}
                    alt={`${venue.name} in ${destinationData.name}`}
                    className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                    style={{ display: 'block' }}
                  />
                </div>
                <div className="p-4 w-full min-h-[120px]">
                  <div className="mb-2">
                    <h4 className="text-lg font-playfair text-[#181c24] mb-1">{language === 'en' ? venue.name : venue.name_es}</h4>
                    {venue.type && (
                      <span className="text-xs text-[#bfa76a] font-light">{venue.type}</span>
                    )}
                  </div>
                  <p className="text-xs md:text-sm text-[#181c24]/80 font-light">
                    {language === 'en' ? venue.description : venue.description_es}
                  </p>
                </div>
              </div>
            )}
          />
        </CarouselWithArrows>
      </div>
      {/* Desktop carousel (>=1024px) */}
      <div className="hidden lg:block">
        <CarouselWithArrows showArrows={showArrows} ref={desktopContainerRef}>
          <SimpleCarousel
            items={venues}
            isInfinite
            itemWidth={desktopItemWidth}
            renderItem={(venue, i) => (
              <div
                className="bg-white rounded-none shadow-md h-full flex flex-col w-full"
              >
                <div className="w-full h-64 bg-[#f5f0e6]">
                  <img
                    src={venue.imageUrl}
                    alt={`${venue.name} in ${destinationData.name}`}
                    className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                    style={{ display: 'block' }}
                  />
                </div>
                <div className="p-4 flex-1 flex flex-col justify-between w-full">
                  <div className="flex flex-col mb-1">
                    <h4 className="text-lg font-playfair text-[#181c24] mb-0.5">{language === 'en' ? venue.name : venue.name_es}</h4>
                    {venue.type && (
                      <span className="text-xs text-[#bfa76a] font-light">{venue.type}</span>
                    )}
                  </div>
                  <p className="text-xs md:text-sm text-[#181c24]/80 font-light mt-1">
                    {language === 'en' ? venue.description : venue.description_es}
                  </p>
                </div>
              </div>
            )}
          />
        </CarouselWithArrows>
      </div>
      <div className="mt-2 md:mt-12 text-center">
        <p className="text-xs md:text-sm text-[#181c24]/80 font-light max-w-2xl mx-auto mb-2 md:mb-8">
          {t.destinationDetail.venues.blurb.replace('{destination}', destinationData.name)}
        </p>
        <Link
          to="/contact"
          className="inline-block border border-[#bfa76a] text-[#bfa76a] bg-transparent py-1 md:py-3 px-4 md:px-8 rounded-full text-lg font-semibold hover:bg-[#bfa76a] hover:text-[#181c24] transition-all duration-300 text-center"
        >
          {t.destinationDetail.venues.explore}
        </Link>
      </div>
    </section>
  );
};

// Guest Experience Section Component
const ExperiencesSection = ({ destinationData, language, t }) => (
  <section id="experiences" className="max-w-6xl mx-auto px-6 py-2 md:py-16 bg-white scroll-mt-[9.5rem]">
    <h2 className="text-3xl md:text-5xl font-playfair mb-2 md:mb-10 text-center text-[#181c24] tracking-tight">
      {t.destinationDetail.experiences.title.replace('{destination}', destinationData.name)}
    </h2>
    {/* Image full width */}
    <div className="flex justify-center mb-2 md:mb-10">
      <img
        src={destinationData.experienceImage || destinationData.image}
        alt={`Climate and scenery in ${destinationData.name}`}
        className="w-full max-w-4xl rounded-lg shadow-lg object-cover object-center border border-[#e5d9c3] h-56 md:h-96"
        loading="lazy"
        onError={e => { e.target.onerror = null; e.target.src = destinationData.experienceImage; }}
      />
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-10 items-center mb-2">
      {/* Refined luxury blurb card */}
      <div className="bg-[#fcf8f2] rounded-xl p-4 md:p-8 shadow-sm flex flex-col justify-center max-w-2xl mx-auto w-full relative pl-4 md:pl-8">
        {/* Gold accent bar, aligned with headline */}
        <div className="absolute left-0 top-8 h-8 w-1 bg-[#bfa76a] rounded-full" />
        <div className="w-full">
          <span className="block font-playfair font-bold text-lg md:text-xl text-[#181c24] leading-snug mb-1 md:mb-2">
            {destinationData.experienceBlurb || t.destinationDetail.experiences.blurbDefault}
          </span>
          <span className="block text-xs md:text-sm font-inter text-[#181c24]/90 leading-relaxed">
            {t.destinationDetail.experiences.blurb.replace('{destination}', destinationData.name)}
          </span>
        </div>
      </div>
      {/* Guest Journeys List */}
      <div className="max-w-2xl mx-auto w-full flex flex-col justify-center">
        <h3 className="text-xl md:text-2xl font-playfair mb-2 md:mb-6 text-[#181c24]">{t.destinationDetail.experiences.curated}</h3>
        <ul className="space-y-2 md:space-y-5">
          {destinationData.curatedActivities.map((activity, i) => (
            <li key={i} className="flex items-start gap-2 md:gap-3">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="#bfa76a" className="w-5 h-5 mt-1 flex-shrink-0">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-sm md:text-base text-[#181c24]/90 font-light">{activity}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

// Final CTA Section Component
const FinalCTASection = ({ destinationData, language, t }) => {
  const cta = t.destinationDetail?.cta || {};
  return (
  <section className="relative py-20 px-4 md:px-0 bg-[#181c24] text-white text-center overflow-hidden">
    <div className="relative z-10 max-w-3xl mx-auto flex flex-col items-center justify-center">
      <h2 className="text-3xl md:text-4xl font-playfair mb-4 text-[#f7e6df]">
          {cta.title ? cta.title.replace('{destination}', destinationData.name) : ''}
      </h2>
      <div className="flex flex-col md:flex-row gap-4">
        <Link
          to="/contact"
            className="border border-[#bfa76a] text-[#bfa76a] bg-transparent py-4 px-10 rounded-full text-lg font-semibold shadow-sm hover:bg-[#bfa76a] hover:text-[#181c24] transition-all duration-300 font-playfair text-center"
        >
            {cta.schedule || ''}
        </Link>
        </div>
    </div>
  </section>
  );
};

// Add new components for Accommodations and Restaurants
const ThumbnailStrip = ({ items, renderItem, activeIndex, onThumbnailClick }) => {
    const stripRef = useRef(null);

    // Scroll active thumbnail into view
    useEffect(() => {
        if (stripRef.current) {
            const activeChild = stripRef.current.children[activeIndex];
            if (activeChild) {
                activeChild.scrollIntoView({
                    behavior: 'smooth',
                    inline: 'center',
                    block: 'nearest',
                });
            }
        }
    }, [activeIndex]);

    return (
        <div ref={stripRef} className="flex justify-center gap-2 overflow-x-auto p-2 scrollbar-hide">
            {items.map((item, i) => (
                <button key={i} onClick={() => onThumbnailClick(i)} className="rounded-lg focus:outline-none focus:ring-2 focus:ring-[#bfa76a] focus:ring-offset-2">
                    {renderItem(item, i === activeIndex)}
                </button>
            ))}
        </div>
    );
};

const AccommodationsSection = ({ destinationData, language, t, showArrows }) => {
  const hotels = (destinationData.hotelsResortsStays || []).slice(0, 6);

  const [itemWidth, setItemWidth] = useState('calc(100vw - 82px)');
  const containerRef = useRef(null);

  useEffect(() => {
    const calculateWidth = () => {
      if (!containerRef.current) return;
      // Always show 1 card on mobile/tablet views for simplicity and focus
      setItemWidth('calc(100vw - 82px)');
    };

    const timeoutId = setTimeout(calculateWidth, 50);
    window.addEventListener('resize', calculateWidth);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', calculateWidth);
    };
  }, [hotels]);

  return (
    <section id="accommodation" className="max-w-6xl mx-auto px-4 sm:px-6 py-8 scroll-mt-[9.5rem]">
      <h2 className="text-2xl md:text-5xl font-playfair mb-8 md:mb-12 text-center text-[#181c24] tracking-tight">
        {t.destinationDetail.accommodation.title}
      </h2>
      {/* Mobile simple carousel */}
      <div className="md:hidden">
        <CarouselWithArrows 
            showArrows={showArrows} 
            ref={containerRef}
            items={hotels}
            renderThumbnail={(hotel, isActive) => (
                <img
                    src={hotel.imageUrl || hotel.image}
                    alt={hotel.name}
                    className={`w-16 h-16 object-cover rounded-md border-2 ${isActive ? 'border-[#bfa76a]' : 'border-transparent'} transition-all`}
                />
            )}
        >
          <SimpleCarousel
            items={hotels}
            isInfinite
            itemWidth={itemWidth}
            renderItem={(hotel) => (
              <div className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col w-full h-full">
                <div className="w-full h-72 overflow-hidden flex-shrink-0">
                  <img
                    src={hotel.imageUrl || hotel.image}
                    alt={`${hotel.name} in ${destinationData.name}`}
                    className="w-full h-full object-cover object-center"
                    loading="lazy"
                  />
                </div>
                <div className="flex flex-col justify-between flex-1 p-4 w-full">
                  <div>
                    <h4 className="text-lg font-playfair text-[#181c24] mb-0.5">{getLocalized(hotel, 'name', language)}</h4>
                    <p className="text-xs md:text-sm text-[#181c24]/80 font-light mt-1">
                      {getLocalized(hotel, 'description', language)}
                    </p>
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <span className="text-[#bfa76a]">{hotel.rating}</span>
                  </div>
                </div>
              </div>
            )}
          />
        </CarouselWithArrows>
      </div>
      {/* Desktop grid ... only on md+ */}
      <div className="hidden md:grid grid-cols-3 gap-6">
        {hotels.map((hotel, i) => (
          <div key={i} className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col w-full">
            <div className="w-full h-64 overflow-hidden flex-shrink-0">
              <img
                src={hotel.imageUrl || hotel.image}
                alt={`${hotel.name} in ${destinationData.name}`}
                className="w-full h-full object-cover object-center"
                loading="lazy"
              />
            </div>
            <div className="flex flex-col justify-between flex-1 p-4 w-full">
              <div>
                <h4 className="text-lg font-playfair text-[#181c24] mb-0.5">{getLocalized(hotel, 'name', language)}</h4>
                <p className="text-xs md:text-sm text-[#181c24]/80 font-light mt-1">
                  {getLocalized(hotel, 'description', language)}
                </p>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <span className="text-[#bfa76a]">{hotel.rating}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

const RestaurantsSection = ({ destinationData, language, t, showArrows }) => {
  const restaurants = (destinationData.diningExperiences || []).slice(0, 6);

  const [itemWidth, setItemWidth] = useState('calc(100vw - 82px)');
  const containerRef = useRef(null);

  useEffect(() => {
    const calculateWidth = () => {
      if (!containerRef.current) return;
      // Always show 1 card on mobile/tablet views for simplicity and focus
      setItemWidth('calc(100vw - 82px)');
    };

    const timeoutId = setTimeout(calculateWidth, 50);
    window.addEventListener('resize', calculateWidth);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', calculateWidth);
    };
  }, [restaurants]);

  return (
    <section id="dining" className="max-w-6xl mx-auto px-4 sm:px-6 py-8 scroll-mt-[9.5rem]">
      <h2 className="text-2xl md:text-5xl font-playfair mb-8 md:mb-12 text-center text-[#181c24] tracking-tight">
        {t.destinationDetail.dining.title}
      </h2>
      {/* Mobile simple carousel */}
      <div className="md:hidden">
        <CarouselWithArrows 
            showArrows={showArrows} 
            ref={containerRef}
            items={restaurants}
            renderThumbnail={(restaurant, isActive) => (
                <img
                    src={restaurant.imageUrl || restaurant.image}
                    alt={restaurant.name}
                    className={`w-16 h-16 object-cover rounded-md border-2 ${isActive ? 'border-[#bfa76a]' : 'border-transparent'} transition-all`}
                />
            )}
        >
          <SimpleCarousel
            items={restaurants}
            isInfinite
            itemWidth={itemWidth}
            renderItem={(restaurant) => (
              <div className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col w-full h-full">
                <div className="w-full h-72 overflow-hidden flex-shrink-0">
                  <img
                    src={restaurant.imageUrl || restaurant.image}
                    alt={`${restaurant.name} in ${destinationData.name}`}
                    className="w-full h-full object-cover object-center"
                    loading="lazy"
                  />
                </div>
                <div className="flex flex-col justify-between flex-1 p-4 w-full">
                  <div>
                    <h4 className="text-lg font-playfair text-[#181c24] mb-0.5">{getLocalized(restaurant, 'name', language)}</h4>
                    <p className="text-xs md:text-sm text-[#181c24]/80 font-light mt-1">
                      {getLocalized(restaurant, 'description', language)}
                    </p>
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <span className="text-[#bfa76a]">{restaurant.rating}</span>
                  </div>
                </div>
              </div>
            )}
          />
        </CarouselWithArrows>
      </div>
      {/* Desktop grid ... only on md+ */}
      <div className="hidden md:grid grid-cols-3 gap-6">
        {restaurants.map((restaurant, i) => (
          <div key={i} className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col w-full">
            <div className="w-full h-64 overflow-hidden flex-shrink-0">
              <img
                src={restaurant.imageUrl || restaurant.image}
                alt={`${restaurant.name} in ${destinationData.name}`}
                className="w-full h-full object-cover object-center"
                loading="lazy"
              />
            </div>
            <div className="flex flex-col justify-between flex-1 p-4 w-full">
              <div>
                <h4 className="text-lg font-playfair text-[#181c24] mb-0.5">{getLocalized(restaurant, 'name', language)}</h4>
                <p className="text-xs md:text-sm text-[#181c24]/80 font-light mt-1">
                  {getLocalized(restaurant, 'description', language)}
                </p>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <span className="text-[#bfa76a]">{restaurant.rating}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

// Utility function for language selection
function getLocalized(item, field, language) {
  if (!item) return '';
  if (language === 'en') return item[field] || item[`${field}_es`] || '';
  return item[`${field}_es`] || item[field] || '';
}

export default function DestinationDetail() {
  const { slug } = useParams();
  const { language } = useLanguage();
  const t = language === 'en' ? translations : esTranslations;
  const destination = destinationsData.find(d => d.slug === slug);
  const [visibleArrows, setVisibleArrows] = useState(null);
  const arrowTimeoutRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const sections = {
        venues: document.getElementById('venues'),
        accommodation: document.getElementById('accommodation'),
        dining: document.getElementById('dining'),
      };

      let mostVisibleSection = null;
      let maxVisibility = 0;

      for (const id in sections) {
        const el = sections[id];
        if (el) {
          const rect = el.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const carousel = el.querySelector('.overflow-x-auto');
          if (!carousel) continue;

          // Only consider carousels that are currently displayed
          if (carousel.offsetParent === null) continue;

          // Calculate how much of the element is visible
          const visibleHeight = Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0));
          const visibility = visibleHeight / rect.height;

          // Check if it's in the active portion of the screen
          const isInActiveZone = rect.top < (viewportHeight * 0.75) && rect.bottom > 100;

          if (isInActiveZone && visibility > maxVisibility) {
            maxVisibility = visibility;
            mostVisibleSection = id;
          }
        }
      }

      // Show arrows, then hide after timeout
      if (mostVisibleSection) {
        setVisibleArrows(mostVisibleSection);
        if (arrowTimeoutRef.current) clearTimeout(arrowTimeoutRef.current);
        arrowTimeoutRef.current = setTimeout(() => {
          setVisibleArrows(null);
        }, 1500); // Hide after 1.5s
      }
    };

    const throttledScroll = () => {
      window.requestAnimationFrame(handleScroll);
    };

    window.addEventListener('scroll', throttledScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', throttledScroll);
      if (arrowTimeoutRef.current) clearTimeout(arrowTimeoutRef.current);
    };
  }, []);

  if (!destination) {
    return (
      <div className="font-inter bg-white text-[#181c24] min-h-screen">
        <div className="flex flex-col items-center justify-center h-[60vh]">
          <h1 className="text-3xl font-playfair mb-4">{t.destinations.notFound}</h1>
          <Link to="/destinations" className="text-[#bfa76a]">{t.destinations.backToDestinations}</Link>
        </div>
      </div>
    );
  }

  // Transform destination data to match new structure
  const destinationData = {
    name: language === 'en' ? destination.title : destination.title_es,
    slug: destination.slug,
    heroImage: destination.heroImage,
    magicImage: destination.magicImage,
    experienceImage: destination.experienceImage,
    image: destination.image,
    footerSliverImage: destination.footerSliverImage,
    introHookText: language === 'en' ? destination.subtitle : destination.subtitle_es,
    magicExperience: language === 'en' ? (destination.magicExperience || destination.magicExperience_es) : (destination.magicExperience_es || destination.magicExperience),
    vibe: language === 'en' ? (destination.vibe || destination.vibe_es) : (destination.vibe_es || destination.vibe),
    climateSeasons: language === 'en' ? (destination.climateSeasons || destination.climateSeasons_es) : (destination.climateSeasons_es || destination.climateSeasons),
    accessibilityDetails: language === 'en'
      ? (destination.accessibilityDetails || destination.accessibilityDetails_es)
      : (destination.accessibilityDetails_es || destination.accessibilityDetails),
    weddingVenues: destination.weddingVenues,
    hotelsResortsStays: destination.hotelsResortsStays,
    diningExperiences: destination.diningExperiences,
    curatedActivities: language === 'en' ? destination.topActivities : destination.topActivities_es,
    experienceBlurb: language === 'en' ? (destination.experienceBlurb || destination.experienceBlurb_es) : (destination.experienceBlurb_es || destination.experienceBlurb),
    favoriteWeddingSeason: language === 'en' ? (destination.favoriteWeddingSeason || destination.favoriteWeddingSeason_es) : (destination.favoriteWeddingSeason_es || destination.favoriteWeddingSeason),
    logisticsGettingThere: "From private chauffeured transfers...",
    logisticsAccommodation: "We facilitate Assemblage lodging solutions...",
    budgetIntroText: "A wedding is not just an event; it's a curated multi-day experience...",
    budgetCategories: [
      { name: 'Venue', range: '$5,000 - $15,000 USD' },
      { name: 'Catering', range: '$100 - $200 per person' },
      { name: 'Photography/Videography', range: '$3,000 - $8,000 USD' },
      { name: 'Floral & Decor', range: '$4,000 - $12,000 USD' },
      { name: 'Entertainment', range: '$2,000 - $6,000 USD' },
      { name: 'Planning Fees', range: '$3,000 - $8,000 USD' }
    ],
    faqs: [
      {
        question: "What is the best time of year to get married in this destination?",
        answer: "The ideal time depends on your preferences..."
      },
      {
        question: "How far in advance should we book our wedding?",
        answer: "We recommend booking 12-18 months in advance..."
      }
    ],
  };

  return (
    <div className="font-inter bg-white text-[#181c24] min-h-screen">
      {/* Hero Section */}
      <section className="relative h-48 md:h-[40vh] lg:h-[50vh] flex items-end overflow-hidden">
        <img
          src={destinationData.heroImage}
          alt={destinationData.name}
          className="absolute inset-0 w-full h-full object-cover object-center z-0"
          loading="eager"
          fetchpriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#181c24]/90 via-[#23283a]/60 to-transparent" />
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 pb-8 pt-8 sm:pt-0 sm:pb-[72px] text-left">
          <h1 className="text-3xl xs:text-4xl sm:text-5xl md:text-7xl font-playfair text-white mb-2 sm:mb-4 drop-shadow-[0_4px_24px_rgba(24,28,36,0.9)] tracking-tight break-words leading-tight">
            {destinationData.name}
          </h1>
          <p className="hidden sm:block text-base text-[#f7e6df] max-w-2xl">
            {destinationData.introHookText}
          </p>
        </div>
      </section>
      {/* Add extra spacing below hero on mobile */}
      <div className="block sm:hidden h-4" />

      {/* Table of Contents - Hidden on mobile */}
      <section className="hidden md:block px-6 sm:px-12 bg-white/95 backdrop-blur-sm border-b border-[#bfa76a]/20 sticky top-20 z-40 py-4 md:py-6 overflow-x-auto">
        <div className="max-w-5xl mx-auto flex items-center min-h-[64px]">
          <nav className="flex flex-nowrap gap-4 md:gap-8 text-sm md:text-base whitespace-nowrap">
            <a href="#why-destination" className="text-[#181c24] hover:text-[#bfa76a]">{language === 'en' ? `Why ${destinationData.name}` : `¿Por qué ${destinationData.name}?`}</a>
            <a href="#venues" className="text-[#181c24] hover:text-[#bfa76a]">{language === 'en' ? 'Dream Venues' : 'Lugares de Ensueño'}</a>
            <a href="#experiences" className="text-[#181c24] hover:text-[#bfa76a]">{language === 'en' ? 'Experiences' : 'Experiencias'}</a>
            <a href="#accommodation" className="text-[#181c24] hover:text-[#bfa76a]">{language === 'en' ? 'Accommodation' : 'Alojamiento'}</a>
            <a href="#dining" className="text-[#181c24] hover:text-[#bfa76a]">{language === 'en' ? 'Dining' : 'Gastronomía'}</a>
          </nav>
        </div>
      </section>

      {/* Main Content Grid */}
      <div className="max-w-5xl mx-auto px-6 sm:px-12 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Content Column */}
          <div className="lg:col-span-3 space-y-8">
            {/* Why Destination Section */}
            <WhyDestinationSection destinationData={destinationData} language={language} />
            <div className="my-2 md:my-8 flex justify-center"><div className="h-0.5 w-24 bg-[#bfa76a] rounded-full" /></div>
            {/* Assemblage Info for mobile/tablet */}
            <div className="block lg:hidden">
              <AssemblageInfo />
            </div>
            {/* Dream Venues Section */}
            <DreamVenuesSection destinationData={destinationData} language={language} t={t} showArrows={visibleArrows === 'venues'} />
            <div className="my-2 md:my-8 flex justify-center"><div className="h-0.5 w-24 bg-[#bfa76a] rounded-full" /></div>
            {/* Guest Experiences Section */}
            <ExperiencesSection destinationData={destinationData} language={language} t={t} />
            <div className="my-2 md:my-8 flex justify-center"><div className="h-0.5 w-24 bg-[#bfa76a] rounded-full" /></div>
            {/* Accommodations Section (uses SimpleCarousel for mobile, grid for desktop) */}
            <AccommodationsSection destinationData={destinationData} language={language} t={t} showArrows={visibleArrows === 'accommodation'} />
            <div className="my-2 md:my-8 flex justify-center"><div className="h-0.5 w-24 bg-[#bfa76a] rounded-full" /></div>
            {/* Restaurants Section (uses SimpleCarousel for mobile, grid for desktop) */}
            <RestaurantsSection destinationData={destinationData} language={language} t={t} showArrows={visibleArrows === 'dining'} />
            {/* Vendor Network & Collaboration Section 
            <VendorNetworkSection destinationData={destinationData} language={language} t={t} />*/}
          </div>

          {/* Right Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-[11rem] space-y-8 hidden lg:block pt-10">
              {/* Assemblage Info for desktop */}
              <AssemblageInfo />
              {/* Contact Us Blurb */}
              <div className="bg-[#181c24] rounded-xl p-6 shadow-sm mt-6 flex flex-col items-start">
                <h3 className="text-lg font-playfair mb-2 text-[#bfa76a]">{language === 'en' ? 'Start Your Journey' : 'Comienza tu Viaje'}</h3>
                <p className="text-[10px] md:text-xs text-[#ffffff]/90 font-light mb-4">{language === 'en' ? 'Ready to start planning or have questions? Reach out to our team for a complimentary consultation.' : '¿Listo para empezar a planear o tienes preguntas? Contáctanos para una consulta gratuita.'}</p>
                <Link 
                  to="/contact" 
                  onClick={trackConsultationClick}
                  className="inline-block border border-[#bfa76a] text-[#bfa76a] bg-transparent py-2 px-6 rounded-full text-sm font-medium hover:bg-[#bfa76a] hover:text-[#181c24] hover:border-[#bfa76a] transition-all duration-300 text-center"
                >
                  {language === 'en' ? 'Schedule a Consultation' : 'Agendar una Consulta'}
                </Link>
              </div>
            </div>
          </div>
        </div>
        </div>

      {/* Final CTA Section */}
      <FinalCTASection destinationData={destinationData} language={language} t={t} />

      {/* Full-bleed image sliver above footer */}
      <FullBleedImage 
        src={destinationData.footerSliverImage} 
        alt={`Scenic view of ${destinationData.name}`}
        objectPosition={destinationData.slug === 'mexico-city' ? 'center 50%' : 'center'}
      />
    </div>
  );
}
